/**
 * Riftgate Arena — reading the TEMPLATE's translations over HTTP.
 *
 * The game's own UI strings are a bundle chunk (see `catalogue.ts`). Core's
 * `errors` / `emails` / `currencies` namespaces are not ours to ship: they live
 * in `backend/src/core/i18n/locales/*.json`, they change when the template is
 * merged, and the backend already serves them at `GET /i18n/:locale` with a
 * 5-minute cache header. So we fetch them and mount the result under the
 * reserved `core.` namespace — `t('core.errors.not_found')` — which keeps the
 * two catalogues merged for lookups and unmerged in every other sense.
 *
 * ── THIS FETCH MUST NEVER BLOCK OR BREAK FIRST PAINT ────────────────────────
 * Every function here resolves to `null` on failure instead of throwing. The
 * game is fully usable without core's namespace (a translated API error becomes
 * the server's own English message, which is accurate if not localised); it is
 * NOT usable if a slow backend holds up the first render, and it must not be
 * unusable because the backend is down.
 *
 * ── THE FIVE LANGUAGES CORE DOES NOT HAVE ───────────────────────────────────
 * `core/i18n/i18n.constants.ts` lists en_GB, de_DE, pl_PL, nb_NO, sv_SE, hu_HU
 * (with cs_CZ/fr_FR/es_ES files present but off by default, enabled via the
 * `LOCALES` env var). ru_RU, lv_LV, pt_BR, tr_TR and ja_JP do not exist there
 * at all, and that file is core-owned so we may not add them. A request for one
 * of those 404s, and we retry once against core's own default locale, so a
 * Japanese player sees English error text rather than raw keys.
 */
import { api } from '../../core/network/api';
import type { Catalogue } from './translate';
import { toCoreLocale, type Locale } from './locales';

/** Shape of `GET /i18n/config`. */
export interface CoreI18nConfig {
  readonly locales: ReadonlyArray<{ code: string; tag: string; label: string }>;
  readonly defaultLocale: string;
  readonly currencies: ReadonlyArray<{ code: string; symbol: string }>;
  readonly defaultCurrency: string;
}

export interface CoreBundle {
  /** The core locale actually served. */
  readonly locale: string;
  /** What we asked for, which differs when the language is not enabled. */
  readonly requested: string;
  /** True when `locale !== requested`, i.e. these strings are in the wrong language. */
  readonly fellBack: boolean;
  readonly messages: Catalogue;
}

export type CoreBundleLoader = (locale: Locale) => Promise<CoreBundle | null>;

/** Last resort if `/i18n/config` is unreachable — core's own documented default. */
const CORE_FALLBACK_LOCALE = 'en_GB';

let configPromise: Promise<CoreI18nConfig | null> | null = null;
const bundleCache = new Map<string, CoreBundle>();

/** Fetch and cache `GET /i18n/config`. Resolves to null when the API is unavailable. */
export function fetchCoreConfig(): Promise<CoreI18nConfig | null> {
  if (configPromise !== null) return configPromise;
  configPromise = api
    .get<CoreI18nConfig>('/i18n/config')
    .then((config) => (isConfig(config) ? config : null))
    .catch(() => null);
  return configPromise;
}

function isConfig(value: unknown): value is CoreI18nConfig {
  return (
    value !== null &&
    typeof value === 'object' &&
    Array.isArray((value as CoreI18nConfig).locales) &&
    typeof (value as CoreI18nConfig).defaultLocale === 'string'
  );
}

/**
 * Is this language enabled on the backend right now?
 *
 * Answered from `/i18n/config` (authoritative — a deployment can enable es_ES
 * through the `LOCALES` env var), and `null` when we could not ask. Callers
 * treat null as "unknown, try anyway and tolerate a 400", never as "no".
 */
export async function coreLocaleEnabled(locale: Locale): Promise<boolean | null> {
  const config = await fetchCoreConfig();
  if (config === null) return null;
  const wanted = toCoreLocale(locale);
  return config.locales.some((entry) => entry.code === wanted);
}

/**
 * Fetch core's messages for a language, falling back to core's default locale
 * when that language is not enabled there.
 */
export const fetchCoreBundle: CoreBundleLoader = async (locale) => {
  const requested = toCoreLocale(locale);
  const config = await fetchCoreConfig();
  const coreDefault = config?.defaultLocale ?? CORE_FALLBACK_LOCALE;

  const enabled = config === null ? null : config.locales.some((e) => e.code === requested);
  // When config says it is not enabled, do not spend a round trip on a 404.
  const target = enabled === false ? coreDefault : requested;

  const first = await getBundle(target, requested);
  if (first !== null) return first;
  if (target === coreDefault) return null;
  return getBundle(coreDefault, requested);
};

async function getBundle(target: string, requested: string): Promise<CoreBundle | null> {
  const cached = bundleCache.get(target);
  if (cached !== undefined) {
    return { ...cached, requested, fellBack: cached.locale !== requested };
  }
  try {
    const body = await api.get<{ locale: string; messages: unknown }>(`/i18n/${target}`);
    const messages = body?.messages;
    if (messages === null || typeof messages !== 'object' || Array.isArray(messages)) return null;
    const bundle: CoreBundle = {
      locale: typeof body.locale === 'string' ? body.locale : target,
      requested,
      fellBack: target !== requested,
      messages: messages as Catalogue,
    };
    bundleCache.set(target, bundle);
    return bundle;
  } catch {
    return null;
  }
}

/** Tests only. */
export function clearCoreBundleCache(): void {
  bundleCache.clear();
  configPromise = null;
}
